import { getUserAccessLevel } from "../helpers/featureAccess";
import { formatFixtureResult } from "../helpers/fixtureResult";
import i18next from "i18next";
import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { fetchFromApi } from "../helpers/fetchFromApi";
import {
  deterministicScore,
  canOracleFixture,
  requestPlayableLeagueForecasts,
  obtainOracle,
  oracleDateRange,
  oracleRequest,
  requestOracleForecasts,
  ORACLE_MODES,
  ORACLE_TEMPOS,
  readOracle,
  scoreAtMinute,
  searchOracleFixtures,
  selectionKey,
  selectOracleFixture,
} from "../helpers/oracle";
import "./OracleWorkspace.css";

const oracleText = (de, en) => (i18next.language || "de").startsWith("de") ? de : en;
const modeTitle = mode => oracleText(ORACLE_MODES[mode].title, {oracle:"THE ORACLE",exact:"MOST LIKELY SCORE",tip:"SCORE PREDICTION"}[mode]);
const when = (value) =>
  new Date(value).toLocaleString(oracleText("de-DE", "en-GB"), {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
const scoreText = (score) =>
  score ? `${score.home} : ${score.away}` : "– : –";
function Crest({ team }) {
  const [failed, setFailed] = useState(false);
  return team.image && !failed ? (
    <img
      className="or-crest"
      src={team.image}
      alt={`Wappen ${team.name}`}
      onError={() => setFailed(true)}
    />
  ) : (
    <span className="or-crest or-crest-fallback" aria-label={team.name}>
      {team.name.slice(0, 2).toUpperCase()}
    </span>
  );
}
export function MatchCard({
  fixture,
  mode,
  result,
  minute,
  active = false,
  revealed = false,
  children,
}) {
  const text = (de, en) => (i18next.language || "de").startsWith("de") ? de : en;
  const goalList = useRef(null);
  useEffect(() => {
    const list = goalList.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [minute]);
  const playing = active && mode === "oracle" && minute !== null;
  const score = playing
    ? scoreAtMinute(result, minute)
    : revealed
    ? result
    : null;
  const events = playing
    ? (result?.goalEvents || []).filter((e) => e.minute <= minute)
    : revealed ? result?.goalEvents || [] : [];
  return (
    <article
      className={`or-match ${active ? "is-active" : ""} ${
        revealed ? "is-revealed" : ""
      }`}
      data-testid="oracle-match"
    >
      <div className="or-match-meta">
        <span>{fixture.league}</span>
        <time dateTime={fixture.kickoff}>{when(fixture.kickoff)}</time>
      </div>
      <div className="or-team-line">
        <div className="or-team">
          <Crest team={fixture.home} />
          <strong>{fixture.home.name}</strong>
        </div>
        <div className="or-score" aria-label="Spielstand">
          {scoreText(score)}
        </div>
        <div className="or-team">
          <Crest team={fixture.away} />
          <strong>{fixture.away.name}</strong>
        </div>
      </div>
      <div className="or-card-mode">{modeTitle(mode)}</div>
      {(playing || (revealed && mode === "oracle" && result)) && (
        <div className="or-live">
          {playing && <><div className="or-clock">
            <span>
              {minute >= 90
                ? text("ABPFIFF", "FULL TIME")
                : minute === 45
                ? text("HALBZEIT", "HALF TIME")
                : text("SPIELMINUTE", "MATCH MINUTE")}
            </span>
            <b>{minute}′</b>
          </div>
          <div className="or-progress">
            <i style={{ width: `${(minute / 90) * 100}%` }} />
          </div>
          </>}
          {((!playing && revealed) || minute >= 45) && (
            <div className="or-half" data-testid="oracle-halftime">
              {text("Halbzeit", "Half time")} {scoreText(result.ht)}
            </div>
          )}
          <div className="or-goals" aria-live="polite" ref={goalList}>
            {events.length ? (
              events.map((e, i) => (
                <div key={i}>
                  <b>{e.minute}′</b>
                  <span>
                    {e.scorer?.name || text("Torschütze nicht bestimmt", "Scorer not determined")}<small style={{display:"block"}}>{text("Simuliert", "Simulated")} · {e.side === "home" ? fixture.home.name : fixture.away.name}</small>
                  </span>
                  <strong>{e.score}</strong>
                </div>
              ))
            ) : (
              <span>{playing ? text("Der nächste Moment gehört dem Spiel.", "Waiting for the next moment.") : text("Keine Tore.", "No goals.")}</span>
            )}
          </div>
        </div>
      )}

      {children}
    </article>
  );
}

export function OracleWorkspace({ user, presentation = false, leagueFixtures = null, onHome }) {
  const userId = user.id;
  const activeProfile = onHome && <span data-testid="oracle-active-profile" style={{fontSize:12,color:"#cbd7df"}}>
    {user.name || user.email || user.user_id || user.id} · {getUserAccessLevel(user).toUpperCase()}
  </span>;
  const isLeague = leagueFixtures !== null;
  const [clock, setClock] = useState(Date.now());
  const [fixtureErrors, setFixtureErrors] = useState({});
  useEffect(() => {
    if (!isLeague) return;
    const timer = setInterval(() => setClock(Date.now()), 15000);
    return () => clearInterval(timer);
  }, [isLeague]);
  const [selected, setSelected] = useState(() => {
    if (leagueFixtures) return leagueFixtures;
    try {
      return JSON.parse(
        localStorage.getItem(selectionKey(userId)) || "[]"
      ).slice(0, 3);
    } catch {
      return [];
    }
  });
  const [period, setPeriod] = useState("week"),
    [fixtures, setFixtures] = useState([]),
    [query, setQuery] = useState("");
  const [mode, setMode] = useState(() => {
      const value = new URLSearchParams(window.location.search).get("mode");
      return ORACLE_MODES[value] ? value : "oracle";
    }),
    [forecasts, setForecasts] = useState({}),
    [oracles, setOracles] = useState({});
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [confirmIds, setConfirmIds] = useState(null);
  const [tempo, setTempo] = useState(() => {
      const value = new URLSearchParams(window.location.search).get("tempo");
      return ORACLE_TEMPOS[value] ? value : "normal";
    }),
    [intro, setIntro] = useState(
      () => new URLSearchParams(window.location.search).get("intro") !== "0"
    ),
    [safeArea, setSafeArea] = useState(false);
  const [show, setShow] = useState({
      phase: "preview",
      index: -1,
      minute: null,
    }),
    [controls, setControls] = useState(true);
  const [scale, setScale] = useState(1);
  const run = useRef(0),
    frame = useRef(null),
    alive = useRef(true),
    generating = useRef(false);
  const selectionId = selected.map((f) => f.id).join(",");
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      cancelAnimationFrame(frame.current);
    };
  }, []);
  useEffect(() => {
    const resize = () =>
      setScale(Math.min(window.innerWidth / 1080, window.innerHeight / 1920));
    resize();
    window.addEventListener("resize", resize);
    const keys = (e) => {
      if (e.key === "Escape") setControls(true);
    };
    window.addEventListener("keydown", keys);
    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("keydown", keys);
    };
  }, []);
  useEffect(() => {
    try {
      if (!leagueFixtures) localStorage.setItem(selectionKey(userId), JSON.stringify(selected));
      const saved = {};
      selected.forEach((f) => {
        const value = readOracle(userId, f.id);
        if (value) saved[f.id] = value;
      });
      setOracles(saved);
    } catch (e) {
      setError(e.message);
    }
  }, [selected, userId, leagueFixtures]);
  useEffect(() => {
    if (presentation || leagueFixtures) return;
    let active = true;
    setBusy(true);
    setError("");
    oracleRequest("oracle-search", oracleDateRange(period))
      .then((data) => active && setFixtures(data.fixtures))
      .catch((e) => active && setError(e.message))
      .finally(() => active && setBusy(false));
    return () => {
      active = false;
    };
  }, [period, presentation, leagueFixtures]);
  useEffect(() => {
    if (!selectionId) {
      setForecasts({});
      return;
    }
    let active = true;
    setBusy(true);
    setError("");
    setForecasts({});
    const request = leagueFixtures
      ? requestPlayableLeagueForecasts(leagueFixtures)
      : requestOracleForecasts(selectionId.split(",").map(Number));
    request
      .then((data) => {
        if (active) {
          setFixtureErrors(data.errors || {});
          setForecasts(
            Object.fromEntries(data.forecasts.map((f) => [f.fixture.id, f]))
          );
        }
      })
      .catch((e) => active && setError(e.message))
      .finally(() => active && setBusy(false));
    return () => {
      active = false;
    };
  }, [selectionId, leagueFixtures]);
  useEffect(() => {
    if (leagueFixtures) {
      if (leagueFixtures.map(f=>f.id).join(",") !== selectionId) resetShow();
      setSelected(leagueFixtures);
    }
    // The parent supplies stable fixtures for the selected matchday.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueFixtures]);
  const resultsReady = leagueFixtures ? selected.some(f => canOracleFixture(f, clock) && (forecasts[f.id] || oracles[f.id])) :
    selected.length > 0 &&
    selected.every(
      (f) => forecasts[f.id] || (mode === "oracle" && oracles[f.id])
    );
  const showing = ["intro", "playing"].includes(show.phase);
  function resetShow() {
    run.current++;
    cancelAnimationFrame(frame.current);
    setShow({ phase: "preview", index: -1, minute: null });
    setControls(true);
  }
  function changeMode(value) {
    resetShow();
    setMode(value);
  }
  async function ask(ids, replace = false) {
    if (generating.current) return null;
    generating.current = true;
    setBusy(true);
    setError("");
    try {
      // Revalidate current fixture state, entitlement and basis at the point of a new draw.
      if (leagueFixtures) ids = ids.filter(id => {
        const fixture = selected.find(f => f.id === id);
        return fixture && (canOracleFixture(fixture) || (!replace && readOracle(userId, id)));
      });
      const needed = ids.filter((id) => replace || !readOracle(userId, id));
      let latest = { ...forecasts };
      if (needed.length) {
        const data = leagueFixtures ? await requestPlayableLeagueForecasts(selected.filter(f => needed.includes(f.id))) : await requestOracleForecasts(needed);
        if (leagueFixtures) {
          needed.forEach(id => delete latest[id]);
          if (alive.current) setFixtureErrors(data.errors || {});
        }
        data.forecasts.forEach((f) => {
          latest[f.fixture.id] = f;
        });
        if (alive.current) setForecasts(latest);
      }
      const next = { ...oracles };
      for (const id of ids) {
        const cached = readOracle(userId, id);
        if (leagueFixtures && !(cached && !replace) && !latest[id]) continue;
        next[id] =
          await obtainOracle(userId, latest[id] || {fixture:selected.find(f=>f.id===id),basis:cached?.basis}, {
                replace,
                confirmed: replace,
              });
      }
      if (alive.current) setOracles(next);
      return next;
    } catch (e) {
      if (alive.current) setError(e.message);
      return null;
    } finally {
      generating.current = false;
      if (alive.current) setBusy(false);
    }
  }
  async function startShow() {
    resetShow();
    const sequence = ++run.current;
    const values =
      mode === "oracle" ? await ask(selected.map((f) => f.id)) : {};
    if (mode === "oracle" && !values) return;
    if (!alive.current || sequence !== run.current) return;
    setControls(!presentation);
    const started = performance.now(),
      introMs = intro ? 1800 : 0,
      gameMs = mode === "oracle" ? ORACLE_TEMPOS[tempo].ms : 1800;
    const holdMs = mode === "oracle" ? 1200 : 650,
      slot = gameMs + holdMs;
    const tick = (now) => {
      if (sequence !== run.current || !alive.current) return;
      const elapsed = now - started;
      if (elapsed < introMs)
        setShow({ phase: "intro", index: -1, minute: null });
      else {
        const time = elapsed - introMs,
          index = Math.floor(time / slot);
        if (index >= selected.length) {
          setShow({ phase: "done", index: selected.length - 1, minute: null });
          return;
        }
        const offset = time - index * slot;
        // A short hold at minute 45 makes halftime readable without changing the underlying timeline.
        const ratio = Math.min(1, offset / gameMs),
          progress =
            ratio < 0.45
              ? ratio / 0.9
              : ratio < 0.55
              ? 0.5
              : (ratio - 0.1) / 0.9;
        setShow({
          phase: "playing",
          index,
          minute:
            mode === "oracle" ? Math.min(90, Math.floor(progress * 90)) : null,
        });
      }
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
  }
  const options = searchOracleFixtures(fixtures, query).filter(
    (f) => !selected.some((s) => s.id === f.id)
  );
  function resultFor(f) {
    if (mode === "oracle") return oracles[f.id]?.result;
    return forecasts[f.id] ? deterministicScore(forecasts[f.id], mode) : null;
  }
  function openPresentation() {
    const url = new URL(window.location.href);
    url.search = new URLSearchParams({
      view: "oracle",
      presentation: "1",
      mode,
      tempo,
      intro: intro ? "1" : "0",
    }).toString();
    window.open(url.toString(), "betty-oracle-presentation");
  }
  function closePresentation() {
    if (window.opener) window.close();
    else {
      const url = new URL(window.location.href);
      url.search = "?view=oracle";
      window.location.assign(url);
    }
  }
  const modes = (
    <div className="or-modes" aria-label="Prognoseart">
      {Object.entries(ORACLE_MODES).map(([key, value]) => (
        <button
          key={key}
          aria-pressed={mode === key}
          disabled={showing || busy}
          onClick={() => changeMode(key)}
        >
          {modeTitle(key)}
        </button>
      ))}
    </div>
  );
  const settings = (
    <div className="or-show-settings">
      <div className="or-tempos" aria-label="Tempo">
        {Object.entries(ORACLE_TEMPOS).map(([key, value]) => (
          <button
            key={key}
            aria-pressed={tempo === key}
            disabled={showing}
            onClick={() => setTempo(key)}
          >
            {oracleText(value.label, {short:"SHORT",normal:"NORMAL",long:"LONG"}[key])}
          </button>
        ))}
      </div>
      <label>
        <input
          type="checkbox"
          checked={intro}
          disabled={showing}
          onChange={(e) => setIntro(e.target.checked)}
        />{" "}
        {oracleText("INTRO ANZEIGEN", "SHOW INTRO")}
      </label>
      {presentation && (
        <label>
          <input
            type="checkbox"
            checked={safeArea}
            onChange={(e) => setSafeArea(e.target.checked)}
          />{" "}
          KAMERA-HILFSLINIE
        </label>
      )}
    </div>
  );
  const confirmation = (
    <Dialog
      open={!!confirmIds}
      onClose={() => setConfirmIds(null)}
      maxWidth="sm"
    >
      <DialogTitle>ORAKEL NEU BEFRAGEN …</DialogTitle>
      <DialogContent>
        Das Orakel hat bereits gesprochen. Die Realität spielt sich nur einmal
        ab. Wirklich noch einmal orakeln?
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmIds(null)}>
          NEIN, DIESES ORAKEL BEHALTEN
        </Button>
        <Button
          onClick={async () => {
            const ids = confirmIds;
            setConfirmIds(null);
            resetShow();
            await ask(ids, true);
          }}
        >
          JA, TROTZDEM NEU BEFRAGEN
        </Button>
      </DialogActions>
    </Dialog>
  );
  if (presentation)
    return (
      <main className="or-presentation">
        <div
          className="or-stage-holder"
          style={{ width: 1080 * scale, height: 1920 * scale }}
        >
          <div
            className={`or-stage phase-${show.phase}`}
            data-testid="oracle-stage"
            style={{ transform: `scale(${scale})` }}
          >
            <header className="or-stage-header">
              {activeProfile}
              <div className="or-brand">
                betty<span>ORAKEL / MATCHDAY</span>
              </div>
              <div className="or-kicker">
                {selected.length === 3
                  ? "DREI TOPSPIELE"
                  : selected.length === 2
                  ? "ZWEI TOPSPIELE"
                  : "DEIN TOPSPIEL"}{" "}
                DES WOCHENENDES
              </div>
              <h1>
                Das Spiel ist offen.
                <br />
                <em>Die Bühne gehört betty.</em>
              </h1>
              <div className="or-stage-date">
                {selected
                  .map((f) => new Date(f.kickoff).toLocaleDateString("de-DE"))
                  .filter((v, i, a) => a.indexOf(v) === i)
                  .join(" · ")}
              </div>
            </header>
            <div className="or-stage-mode">
              {modeTitle(mode)}
              <small>{ORACLE_MODES[mode].subtitle}</small>
            </div>
            <div
              className={`or-stage-matches ${
                show.phase === "playing" && mode === "oracle"
                  ? "is-focused"
                  : ""
              }`}
            >
              {selected.map((f, i) => {
                const active = show.phase === "playing" && show.index === i;
                const revealed =
                  show.phase === "done" ||
                  (show.phase === "playing" && i <= show.index);
                return (
                  <div
                    key={f.id}
                    className={
                      active
                        ? "or-featured"
                        : show.phase === "playing" && mode === "oracle"
                        ? "or-away-card"
                        : ""
                    }
                  >
                    <MatchCard
                      fixture={f}
                      mode={mode}
                      result={resultFor(f)}
                      minute={active ? show.minute : null}
                      active={active}
                      revealed={revealed && !!resultFor(f)}
                    />
                  </div>
                );
              })}
            </div>
            {show.phase === "done" && (
              <div className="or-spoken">
                {mode === "oracle"
                  ? "DAS ORAKEL HAT GESPROCHEN"
                  : "DEINE TIPPS STEHEN"}
              </div>
            )}
            <div
              className={`or-camera ${safeArea ? "with-guide" : ""}`}
              data-testid="oracle-camera-area"
            >
              {safeArea && (
                <span>KAMERA · DIESEN BEREICH IN OBS FREIHALTEN</span>
              )}
            </div>
            <footer className="or-stage-footer">
              <strong>
                Kostenloses Tippspiel auf sportwettenvergleich.net
              </strong>
              <span>
                ES GIBT KEINE SICHERHEIT. <b>betty</b>
              </span>
            </footer>
            {show.phase === "intro" && (
              <div className="or-intro">
                <div className="or-intro-ball">
                  <svg viewBox="0 0 100 100" aria-hidden="true">
                    <circle cx="50" cy="50" r="47" fill="#fff3e6" />
                    <path
                      d="M50 25 74 43 65 70H35L26 43Z M4 43 26 43 M35 70 22 91 M65 70 78 91 M74 43 96 43 M50 25V3"
                      fill="#713c59"
                      stroke="#713c59"
                      strokeWidth="5"
                    />
                  </svg>
                </div>
                <b>betty</b>
                <h2>
                  {selected.length === 3
                    ? "Drei Topspiele des Wochenendes"
                    : "Deine Topspiele des Wochenendes"}
                </h2>
                <p>ES GIBT KEINE SICHERHEIT.</p>
              </div>
            )}
          </div>
        </div>
        {controls && (
          <div className="or-presentation-controls">
            {modes}
            {settings}
            {error && <Alert severity="error">{error}</Alert>}
            <button
              className="or-primary"
              disabled={busy || !resultsReady || showing}
              onClick={startShow}
            >
              {show.phase === "done"
                ? "SHOW NOCHMAL ABSPIELEN"
                : "SHOW STARTEN"}
            </button>
            {mode === "oracle" && selected.some((f) => oracles[f.id]) && (
              <button
                disabled={busy || showing}
                onClick={() => setConfirmIds(selected.map((f) => f.id))}
              >
                ORAKEL NEU BEFRAGEN …
              </button>
            )}
            <button
              onClick={() => {
                resetShow();
                closePresentation();
              }}
            >
              ZUR AUSWAHL
            </button>
            <button onClick={closePresentation}>PRÄSENTATION SCHLIESSEN</button>
            <button onClick={() => setControls(false)}>
              BEDIENUNG AUSBLENDEN
            </button>
            <small>
              Esc blendet die Bedienung wieder ein. Vorschau ·{" "}
              {ORACLE_TEMPOS[tempo].label}
            </small>
          </div>
        )}
        {confirmation}
      </main>
    );
  if (leagueFixtures) return (
    <section className="or-workspace or-league" data-testid="league-oracle">
      <p>{i18next.t("help.adminLeague.oracleIntro", { count: selected.length })}</p>
      {modes}
      <p title={i18next.t(`help.adminLeague.oracleHelp.${mode}`)}>{i18next.t(`help.adminLeague.oracleHelp.${mode}`)}</p>
      {settings}
      {error && <Alert severity="error">{error}</Alert>}
      <div className="or-actions">
        <button className="or-primary" disabled={busy || !resultsReady || showing} onClick={startShow}>{i18next.t("help.adminLeague.startOracle")}</button>
        <button disabled={busy || showing || !resultsReady} onClick={() => mode === "oracle" ? setConfirmIds(selected.filter(f => canOracleFixture(f)).map(f => f.id)) : startShow()}>{i18next.t("help.adminLeague.againOracle")}</button>
        {showing && <button onClick={resetShow}>{i18next.t("help.adminLeague.stop")}</button>}
      </div>
      {busy && <p role="status">{i18next.t("help.adminLeague.loading")}</p>}
      {show.phase === "intro" && <div className="or-inline-intro">betty</div>}
      <div className="or-cards">{selected.map((f, i) => <MatchCard key={f.id} fixture={f} mode={mode}
        result={resultFor(f)} minute={show.phase === "playing" && show.index === i ? show.minute : null}
        active={show.phase === "playing" && show.index === i && Boolean(resultFor(f))}
        revealed={Boolean(resultFor(f)) && ((!showing && !canOracleFixture(f, clock) && Boolean(oracles[f.id])) || show.phase === "done" || (show.phase === "playing" && i <= show.index))} >
          <p className="or-real">{canOracleFixture(f, clock) ? (oracles[f.id] ? oracleText("Gespeichertes Orakel", "Saved oracle") : oracleText("Noch nicht orakelt", "No oracle yet")) : `REAL · ${formatFixtureResult(f) || f.state?.name || f.state?.state || "Begonnen / nicht spielbereit"}`}</p>
          {!canOracleFixture(f, clock) && !oracles[f.id] && <small>{oracleText("Kein früheres Orakel vorhanden. Keine neue Ziehung.", "No earlier oracle available. No new draw.")}</small>}
          {fixtureErrors[f.id] && <small>{fixtureErrors[f.id]}</small>}
        </MatchCard>)}</div>
      {confirmation}
    </section>
  );
  return (
    <main className="or-workspace">
      <header className="or-top">
        {activeProfile}
        <a href={window.location.pathname} className="or-brand" onClick={onHome ? (event) => { event.preventDefault(); onHome(); } : undefined}>
          betty<span>{i18next.t("help.start.home")}</span>
        </a>
        {onHome && <button onClick={onHome}>{i18next.t("help.start.home")}</button>}
        <span>DEIN MOMENT. DEINE SPIELE.</span>
      </header>
      <section className="or-heading">
        <div className="or-kicker">DAS BETTY ORAKEL</div>
        <h1>
          Welche Spiele sollen
          <br />
          wir uns ansehen?
        </h1>
        <p>Suche bis zu drei kommende Partien und lass betty sprechen.</p>
      </section>
      <section className="or-selection">
        <div className="or-periods">
          <button
            aria-pressed={period === "week"}
            onClick={() => setPeriod("week")}
          >
            Kommende 7 Tage
          </button>
          <button
            aria-pressed={period === "weekend"}
            onClick={() => setPeriod("weekend")}
          >
            Dieses Wochenende
          </button>
        </div>
        <Autocomplete
          options={options}
          filterOptions={(items) => items}
          inputValue={query}
          onInputChange={(_, value) => setQuery(value)}
          value={null}
          getOptionLabel={(f) => `${f.home.name} – ${f.away.name}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          disabled={selected.length >= 3 || showing}
          loading={busy}
          onChange={(_, fixture) => {
            if (fixture) {
              try {
                setSelected(selectOracleFixture(selected, fixture));
                setQuery("");
                resetShow();
              } catch (e) {
                setError(e.message);
              }
            }
          }}
          noOptionsText="Keine kommende Partie gefunden. Prüfe den Zeitraum oder einen kürzeren Suchbegriff."
          renderInput={(params) => (
            <TextField
              {...params}
              label="Mannschaft oder Paarung eingeben"
              placeholder="Real Atletico · Dresden Hertha · BORU"
            />
          )}
          renderOption={(props, f) => (
            <li {...props} key={f.id}>
              <div>
                <small>
                  {f.league} · {when(f.kickoff)}
                </small>
                <div>
                  <strong>
                    {f.home.name} – {f.away.name}
                  </strong>
                </div>
              </div>
            </li>
          )}
        />
        <div className="or-slots">
          {[0, 1, 2].map((i) => (
            <div key={i}>
              <span>SPIEL {i + 1}</span>
              {selected[i] ? (
                <>
                  <strong>
                    {selected[i].home.name} – {selected[i].away.name}
                  </strong>
                  <small>
                    {selected[i].league} · {when(selected[i].kickoff)}
                  </small>
                  <button
                    aria-label={`Spiel ${i + 1} entfernen`}
                    disabled={showing}
                    onClick={() => {
                      setSelected(selected.filter((_, n) => n !== i));
                      resetShow();
                    }}
                  >
                    Entfernen ×
                  </button>
                </>
              ) : (
                <p>Dein nächstes Topspiel</p>
              )}
            </div>
          ))}
        </div>
      </section>
      {error && <Alert severity="error">{error}</Alert>}
      {busy && <p role="status">betty bereitet deine Spiele vor …</p>}
      {selected.length > 0 && (
        <section className="or-results">
          {modes}
          <p className="or-mode-explanation">{ORACLE_MODES[mode].subtitle}</p>
          <div className="or-cards">
            {selected.map((f, i) => (
              <MatchCard
                key={f.id}
                fixture={f}
                mode={mode}
                result={resultFor(f)}
                minute={
                  show.phase === "playing" && show.index === i
                    ? show.minute
                    : null
                }
                active={show.phase === "playing" && show.index === i}
                revealed={!!resultFor(f) && (!showing || show.phase === "done" || (show.phase === "playing" && i <= show.index))}
              >
                {mode === "oracle" && (
                  <div className="or-card-actions">
                    <button
                      className="or-primary"
                      disabled={
                        busy || showing || (!forecasts[f.id] && !oracles[f.id])
                      }
                      onClick={() => ask([f.id])}
                    >
                      {oracles[f.id] ? "ORAKEL ANZEIGEN" : "ORAKEL BEFRAGEN"}
                    </button>
                    {oracles[f.id] && (
                      <button
                        className="or-secondary"
                        disabled={busy || showing}
                        onClick={() => setConfirmIds([f.id])}
                      >
                        ORAKEL NEU BEFRAGEN …
                      </button>
                    )}
                  </div>
                )}
              </MatchCard>
            ))}
          </div>
          <div className="or-actions">
            {settings}
            <button
              className="or-primary"
              disabled={busy || !resultsReady || showing}
              onClick={startShow}
            >
              {mode === "oracle" && selected.every((f) => oracles[f.id])
                ? "SHOW NOCHMAL ABSPIELEN"
                : "SHOW STARTEN"}
            </button>
            <button
              className="or-presentation-button"
              disabled={busy || !resultsReady}
              onClick={openPresentation}
            >
              PRÄSENTATION STARTEN <span>9:16 ↗</span>
            </button>
            {showing && <button onClick={resetShow}>SHOW ANHALTEN</button>}
          </div>
          {show.phase === "intro" && (
            <div className="or-inline-intro">betty · Deine Topspiele</div>
          )}
          {show.phase === "done" && (
            <div className="or-spoken">
              {mode === "oracle"
                ? "DAS ORAKEL HAT GESPROCHEN"
                : "DEINE TIPPS STEHEN"}
            </div>
          )}
        </section>
      )}
      <footer className="or-footer">
        ES GIBT KEINE SICHERHEIT.{" "}
        <span>Eine Prognose ist kein Versprechen.</span>
      </footer>
      {confirmation}
    </main>
  );
}

export function OracleEntry({ onHome }) {
  const [user, setUser] = useState(null),
    [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    fetchFromApi("get-user")
      .then((r) => r.json())
      .then((value) => {
        if (active) {
          if (!value?.id) throw new Error("Bitte melde dich bei betty an.");
          setUser(value);
        }
      })
      .catch((e) => active && setError(e.message));
    return () => {
      active = false;
    };
  }, []);
  if (!user)
    return (
      <main className="or-entry">
        {error || "Dein Orakel wird vorbereitet …"}
        {error && <a href="/sign-in">Anmelden</a>}
      </main>
    );
  return (
    <OracleWorkspace
      onHome={onHome}
      user={user}
      presentation={
        new URLSearchParams(window.location.search).get("presentation") === "1"
      }
    />
  );
}
